import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { featureReadinessApi } from '@/utils/api';

// Readiness item types matching backend schema
export interface FeatureReadinessItem {
  id: string;
  featureId: string;
  item: string;
  isCompleted: boolean;
  completedAt: string | null;
  completedById: string | null;
  createdAt: string;
  updatedAt: string;
}

// Readiness item metadata (points for each item)
// Keys must match backend Prisma enum: ReadinessItem
export const READINESS_ITEM_POINTS: Record<string, { label: string; points: number }> = {
  REQUIREMENTS_DEFINED: { label: 'Requirements Defined', points: 20 },
  DESIGN_COMPLETED: { label: 'Design Completed', points: 15 },
  TECH_APPROACH_DEFINED: { label: 'Technical Approach Defined', points: 15 },
  DEPENDENCIES_IDENTIFIED: { label: 'Dependencies Identified', points: 10 },
  DEPENDENCIES_RESOLVED: { label: 'Dependencies Resolved', points: 15 },
  ESTIMATES_VALIDATED: { label: 'Estimates Validated', points: 10 },
  TEAM_ASSIGNED: { label: 'Team Assigned', points: 5 },
  ACCEPTANCE_CRITERIA: { label: 'Acceptance Criteria Defined', points: 10 },
};

interface FeatureReadinessState {
  itemsByFeatureId: Record<string, FeatureReadinessItem[]>;
  loadingFeatureIds: string[];
  updatingItemIds: string[];
  error: string | null;
}

const initialState: FeatureReadinessState = {
  itemsByFeatureId: {},
  loadingFeatureIds: [],
  updatingItemIds: [],
  error: null,
};

// API response types - must match backend response structure
interface ReadinessApiResponse {
  items?: FeatureReadinessItem[];
  readinessItems?: FeatureReadinessItem[];
  item?: FeatureReadinessItem;
  readinessItem?: FeatureReadinessItem;
  updatedItems?: FeatureReadinessItem[];
}

// Async thunks
export const fetchReadinessItems = createAsyncThunk(
  'featureReadiness/fetchItems',
  async (featureId: string, { rejectWithValue }) => {
    const response = await featureReadinessApi.getByFeatureId(featureId);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to fetch readiness items');
    }
    const data = response.data as ReadinessApiResponse | FeatureReadinessItem[] | undefined;
    const items = Array.isArray(data) ? data : (data?.readinessItems || data?.items || []);
    return { featureId, items };
  }
);

export const toggleReadinessItem = createAsyncThunk(
  'featureReadiness/toggleItem',
  async (
    { featureId, itemId, isCompleted }: { featureId: string; itemId: string; isCompleted: boolean },
    { rejectWithValue }
  ) => {
    const response = await featureReadinessApi.updateReadinessItem(featureId, itemId, isCompleted);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to update readiness item');
    }
    const data = response.data as ReadinessApiResponse | FeatureReadinessItem | undefined;
    let item: FeatureReadinessItem | undefined;
    if (data && typeof data === 'object') {
      if ('readinessItem' in data && data.readinessItem && typeof data.readinessItem === 'object') {
        item = data.readinessItem as FeatureReadinessItem;
      } else if ('item' in data && data.item && typeof data.item === 'object' && 'id' in data.item) {
        item = data.item as FeatureReadinessItem;
      } else if ('id' in data && 'featureId' in data) {
        item = data as FeatureReadinessItem;
      }
    }
    return { featureId, item };
  }
);

export const bulkUpdateReadiness = createAsyncThunk(
  'featureReadiness/bulkUpdate',
  async (
    { featureId, items }: { featureId: string; items: Array<{ item: string; isCompleted: boolean }> },
    { rejectWithValue }
  ) => {
    const response = await featureReadinessApi.bulkUpdateReadiness(featureId, items);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to bulk update readiness items');
    }
    const data = response.data as ReadinessApiResponse | FeatureReadinessItem[] | undefined;
    const resultItems = Array.isArray(data) ? data : (data?.updatedItems || data?.items || []);
    return { featureId, items: resultItems };
  }
);

const featureReadinessSlice = createSlice({
  name: 'featureReadiness',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    clearFeatureReadiness(state, action: PayloadAction<string>) {
      delete state.itemsByFeatureId[action.payload];
    },
    // Optimistic update for toggling
    optimisticToggle(
      state,
      action: PayloadAction<{ featureId: string; itemId: string; isCompleted: boolean }>
    ) {
      const { featureId, itemId, isCompleted } = action.payload;
      const items = state.itemsByFeatureId[featureId];
      if (items) {
        const item = items.find((i) => i.id === itemId);
        if (item) {
          item.isCompleted = isCompleted;
          item.completedAt = isCompleted ? new Date().toISOString() : null;
        }
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch readiness items
    builder
      .addCase(fetchReadinessItems.pending, (state, action) => {
        state.loadingFeatureIds.push(action.meta.arg);
        state.error = null;
      })
      .addCase(fetchReadinessItems.fulfilled, (state, action) => {
        state.loadingFeatureIds = state.loadingFeatureIds.filter(
          (id) => id !== action.payload.featureId
        );
        state.itemsByFeatureId[action.payload.featureId] = action.payload.items;
      })
      .addCase(fetchReadinessItems.rejected, (state, action) => {
        state.loadingFeatureIds = state.loadingFeatureIds.filter((id) => id !== action.meta.arg);
        state.error = action.payload as string || 'Failed to fetch readiness items';
      });

    // Toggle readiness item
    builder
      .addCase(toggleReadinessItem.pending, (state, action) => {
        state.updatingItemIds.push(action.meta.arg.itemId);
        state.error = null;
      })
      .addCase(toggleReadinessItem.fulfilled, (state, action) => {
        const { featureId, item } = action.payload;
        state.updatingItemIds = state.updatingItemIds.filter(
          (id) => id !== action.meta.arg.itemId
        );
        // Update the item in state
        const items = state.itemsByFeatureId[featureId];
        if (items && item) {
          const index = items.findIndex((i) => i.id === item.id);
          if (index !== -1) {
            items[index] = item;
          }
        }
      })
      .addCase(toggleReadinessItem.rejected, (state, action) => {
        state.updatingItemIds = state.updatingItemIds.filter(
          (id) => id !== action.meta.arg.itemId
        );
        state.error = action.payload as string || 'Failed to update readiness item';
        // Revert optimistic update by refetching would be handled by component
      });

    // Bulk update
    builder
      .addCase(bulkUpdateReadiness.pending, (state, action) => {
        state.loadingFeatureIds.push(action.meta.arg.featureId);
        state.error = null;
      })
      .addCase(bulkUpdateReadiness.fulfilled, (state, action) => {
        const { featureId, items } = action.payload;
        state.loadingFeatureIds = state.loadingFeatureIds.filter(
          (id) => id !== featureId
        );
        state.itemsByFeatureId[featureId] = items;
      })
      .addCase(bulkUpdateReadiness.rejected, (state, action) => {
        state.loadingFeatureIds = state.loadingFeatureIds.filter(
          (id) => id !== action.meta.arg.featureId
        );
        state.error = action.payload as string || 'Failed to bulk update readiness items';
      });
  },
});

export const { clearError, clearFeatureReadiness, optimisticToggle } = featureReadinessSlice.actions;

export default featureReadinessSlice.reducer;
